import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useOrg } from "../org/useOrg";
import type { OrgRole } from "../org/types";

// Wave 20 — global "view scope" state.
//
// Two flavors only: `mine` (records owned by the current user) and `company`
// (every record the user is allowed to see in the active org). Individual
// tabs don't consume this yet — that's wave 22. For now the provider just
// tracks the state, persists it, and gates a header toggle.
//
// This provider is mounted at the app root (inside `<OrgProvider>`). It
// reads/writes the URL `?scope=` param via plain `window.history` + the
// `popstate` event so it can be placed *above* `<RouterProvider>` without
// needing router context.

export type ViewScope = "mine" | "company";

export interface ViewScopeContextValue {
  scope: ViewScope;
  setScope: (next: ViewScope) => void;
  /** True if the current role has any flexibility (can switch). False for
   * roles locked to one view (employee/auditor). */
  isToggleable: boolean;
}

const STORAGE_KEY = "proofly.viewScope.v1";
const URL_PARAM = "scope";

const VALID_SCOPES: ReadonlyArray<ViewScope> = ["mine", "company"];

function isValidScope(value: unknown): value is ViewScope {
  return typeof value === "string" && (VALID_SCOPES as ReadonlyArray<string>).includes(value);
}

function readStoredScope(): ViewScope | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) return null;
    // Accept both plain strings and JSON-encoded strings.
    let parsed: unknown;
    try { parsed = JSON.parse(raw); }
    catch { parsed = raw; }
    return isValidScope(parsed) ? parsed : null;
  } catch { return null; }
}

function writeStoredScope(next: ViewScope): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* swallow */ }
}

function readUrlScope(): ViewScope | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(URL_PARAM);
    return isValidScope(raw) ? raw : null;
  } catch { return null; }
}

function writeUrlScope(next: ViewScope): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(URL_PARAM) === next) return;
    url.searchParams.set(URL_PARAM, next);
    window.history.replaceState(window.history.state, "", url.toString());
  } catch { /* swallow */ }
}

function defaultForRole(role: OrgRole | null): ViewScope {
  if (role === "employee") return "mine";
  return "company";
}

function lockedScope(role: OrgRole | null): ViewScope | null {
  if (role === "employee") return "mine";
  if (role === "auditor") return "company";
  return null;
}

function isRoleToggleable(role: OrgRole | null): boolean {
  if (role == null) return false;
  return role !== "employee" && role !== "auditor";
}

const PLACEHOLDER_VALUE: ViewScopeContextValue = {
  scope: "mine",
  setScope: () => {},
  isToggleable: false,
};

const ViewScopeContext = createContext<ViewScopeContextValue | undefined>(
  undefined,
);

interface ViewScopeProviderProps { children: ReactNode; }

export function ViewScopeProvider({ children }: ViewScopeProviderProps) {
  const { activeRole } = useOrg();

  // Seed synchronously so we don't render "mine" then flip to "company" on
  // first effect. URL wins over localStorage wins over role default.
  const [scope, setScopeState] = useState<ViewScope>(() => {
    const urlScope = readUrlScope();
    if (urlScope) return urlScope;
    const stored = readStoredScope();
    if (stored) return stored;
    return defaultForRole(activeRole);
  });

  // On page-load, if the URL had a valid scope param it should write through
  // to localStorage. Only runs once — URL changes after that go through
  // `setScope` which writes through on its own.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const urlScope = readUrlScope();
    if (urlScope) {
      writeStoredScope(urlScope);
    }
  }, []);

  // Reconcile with role: if the role is locked to a specific scope and our
  // current value disagrees, snap to the locked value (and persist). This
  // also handles the case where a page-load URL says ?scope=mine for an
  // auditor — the lock wins over the URL.
  useEffect(() => {
    const forced = lockedScope(activeRole);
    if (forced && forced !== scope) {
      setScopeState(forced);
      writeStoredScope(forced);
      writeUrlScope(forced);
    }
  }, [activeRole, scope]);

  // Keep in sync with browser nav (back/forward) and cross-tab storage edits.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPop = () => {
      const next = readUrlScope();
      if (next) setScopeState(next);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const next = readStoredScope();
      if (next) setScopeState(next);
    };
    window.addEventListener("popstate", onPop);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const isToggleable = isRoleToggleable(activeRole);

  const setScope = useCallback(
    (next: ViewScope) => {
      if (!isValidScope(next)) return;
      // Role locks: no-op. Employees can't see company data; auditors can't
      // see mine-only data. The provider enforces this so individual call
      // sites don't need to.
      const forced = lockedScope(activeRole);
      if (forced) return;
      setScopeState((prev) => {
        if (prev === next) return prev;
        writeStoredScope(next);
        writeUrlScope(next);
        return next;
      });
    },
    [activeRole],
  );

  const value = useMemo<ViewScopeContextValue>(
    () => (activeRole == null
      ? PLACEHOLDER_VALUE
      : { scope, setScope, isToggleable }),
    [activeRole, scope, setScope, isToggleable],
  );

  return (
    <ViewScopeContext.Provider value={value}>
      {children}
    </ViewScopeContext.Provider>
  );
}

export function useViewScope(): ViewScopeContextValue {
  const ctx = useContext(ViewScopeContext);
  if (!ctx) {
    // Render a safe placeholder rather than throwing — this keeps tests and
    // ad-hoc previews of individual components working without a provider.
    return PLACEHOLDER_VALUE;
  }
  return ctx;
}
